/**
 * Referral code hooks — TanStack Query wrappers for the referral-codes API.
 *
 * Hooks:
 * - useReferralCodes(filters?)      — paginated list of all referral codes (admin)
 * - useReferralCode(id)             — single referral code by ID (admin)
 * - useCreateReferralCode()         — mutation to create a referral code (admin)
 * - useUpdateReferralCode()         — mutation to update a referral code (admin)
 *
 * Note: the API returns snake_case rows matching the DB schema.
 * All hooks are admin-only (super_admin / sales_admin).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, apiPut } from '@/lib/api'

// ── Row type (snake_case — mirrors API response) ──────────────────────────────

export interface ReferralCodeRow {
  id: string
  code: string
  description: string | null
  discount_percent: number
  stripe_coupon_id: string | null
  owner_id: string | null
  reseller_id: string | null
  active: boolean
  usage_count: number
  max_uses: number | null
  expires_at: string | null
  created_at: string
  updated_at: string
  /** Joined: owner user display name */
  owner_name: string | null
  /** Joined: reseller company name */
  reseller_name: string | null
}

// ── Filter type ────────────────────────────────────────────────────────────────

export interface ReferralCodeListFilters {
  active?: boolean
  limit?: number
  offset?: number
}

// ── DTO types ──────────────────────────────────────────────────────────────────

export interface CreateReferralCodeDTO {
  code: string
  description?: string | null
  discountPercent?: number
  stripeCouponId?: string | null
  ownerId?: string | null
  resellerId?: string | null
  maxUses?: number | null
  expiresAt?: string | null
}

export interface UpdateReferralCodeDTO {
  description?: string | null
  discountPercent?: number
  stripeCouponId?: string | null
  maxUses?: number | null
  expiresAt?: string | null
  active?: boolean
}

// ── Response wrappers ─────────────────────────────────────────────────────────

interface ReferralCodesListApiResponse {
  data: ReferralCodeRow[]
}

interface ReferralCodeDetailApiResponse {
  data: ReferralCodeRow
}

// ── Query key factory ──────────────────────────────────────────────────────────

export const referralCodeKeys = {
  all: ['referral-codes'] as const,
  list: (filters?: ReferralCodeListFilters) =>
    [...referralCodeKeys.all, 'list', filters] as const,
  detail: (id: string) => [...referralCodeKeys.all, 'detail', id] as const,
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * Fetch a paginated list of all referral codes (admin only).
 * Includes owner_name and reseller_name from JOINs.
 */
export function useReferralCodes(filters?: ReferralCodeListFilters) {
  const params = new URLSearchParams()
  if (filters?.active !== undefined) params.set('active', String(filters.active))
  if (filters?.limit !== undefined) params.set('limit', String(filters.limit))
  if (filters?.offset !== undefined) params.set('offset', String(filters.offset))
  const qs = params.toString()
  const path = `/api/v1/referral-codes${qs ? `?${qs}` : ''}`

  return useQuery<ReferralCodeRow[]>({
    queryKey: referralCodeKeys.list(filters),
    queryFn: async () => {
      const res = await apiGet<ReferralCodesListApiResponse>(path)
      return res.data
    },
  })
}

/**
 * Fetch a single referral code by ID (admin only).
 * Disabled when id is falsy.
 */
export function useReferralCode(id: string) {
  return useQuery<ReferralCodeRow>({
    queryKey: referralCodeKeys.detail(id),
    queryFn: async () => {
      const res = await apiGet<ReferralCodeDetailApiResponse>(`/api/v1/referral-codes/${id}`)
      return res.data
    },
    enabled: !!id,
  })
}

/**
 * Mutation to create a new referral code (admin only).
 * Invalidates the referral codes list cache on success.
 */
export function useCreateReferralCode() {
  const queryClient = useQueryClient()

  return useMutation<ReferralCodeRow, Error, CreateReferralCodeDTO>({
    mutationFn: async (data) => {
      const res = await apiPost<ReferralCodeDetailApiResponse>('/api/v1/referral-codes', data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: referralCodeKeys.all })
    },
  })
}

/**
 * Mutation to update a referral code (admin only).
 * Invalidates list and detail caches on success.
 */
export function useUpdateReferralCode() {
  const queryClient = useQueryClient()

  return useMutation<ReferralCodeRow, Error, { id: string; data: UpdateReferralCodeDTO }>({
    mutationFn: async ({ id, data }) => {
      const res = await apiPut<ReferralCodeDetailApiResponse>(`/api/v1/referral-codes/${id}`, data)
      return res.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: referralCodeKeys.all })
      queryClient.invalidateQueries({ queryKey: referralCodeKeys.detail(variables.id) })
    },
  })
}
